import { rd } from '@passionware/monads';
import { createReadWriteApiStore } from 'v5/platform/react/readWriteApiStore';
import { createInMemoryApi } from 'v5/platform/ts/inMemoryApi';
import { createLocalStorageApi } from 'v5/platform/ts/localStorageApi';
import {
  ProductionSearchQuery,
  productionQueryUtils,
  productionSearchQuerySchema
} from 'v5/api/production/production.api';
import {
  ResourceSearchQuery,
  resourceQueryUtils,
  resourceSearchQuerySchema
} from 'v5/api/resource/resource.api';
import { z } from 'zod';
import {
  BookingConfirmationTemplateEditorPreference,
  EntityListLayoutPreference,
  FeatureFlagManagerPreference,
  ProductionsListViewPreference,
  ShortcutMapping,
  ShortlistBoardPreferenceApis,
  UserPreferencesService,
  DEFAULT_SHORTLIST_BOARD_VIEW,
  DEFAULT_PINBOARD_TILE_SIZE,
  DEFAULT_FILES_PANEL_TILE_SIZE,
  DEFAULT_RESOURCE_PICKER_RESULT_FIELDS,
  type DashboardViewPreference,
  type ResourcePickerResultFields
} from './UserPreferencesService';
import {
  TILE_SIZES,
  tileSizeOfMinWidth
} from 'v5/patterns/entity-list/tileSize';
import { DASHBOARD_VIEWS } from 'v5/core/routing/dashboardRouting';

function parseCallsheetProductionQuery(value: unknown): ProductionSearchQuery {
  // The callsheet picker has its own first-open default state.
  if (value == null) {
    return productionQueryUtils.ofDefault();
  }

  return productionSearchQuerySchema.parse(value);
}

function parseResourcingAssignResourceQuery(
  value: unknown
): ResourceSearchQuery {
  if (value == null) {
    return resourceQueryUtils.ofDefault();
  }

  return resourceSearchQuerySchema.parse(value) as ResourceSearchQuery;
}

const productionsListViewPreferenceSchema = z.object({
  showResourcingBudgetColumn: z.boolean(),
  budgetChartMode: z.enum(['vsPlanned', 'vsBudget']).optional()
});

function parseProductionsListView(
  value: unknown,
  defaults: ProductionsListViewPreference
): ProductionsListViewPreference {
  if (value == null) {
    return defaults;
  }

  const parsed = productionsListViewPreferenceSchema.safeParse(value);
  if (!parsed.success) {
    return defaults;
  }

  return {
    ...defaults,
    ...parsed.data,
    budgetChartMode: parsed.data.budgetChartMode ?? defaults.budgetChartMode
  };
}

/**
 * `boardApis`: where the shortlist board's preferences are kept, wired in `.connected`; without
 * them (stories, tests) they live in memory.
 */
export function createUserPreferencesService(
  boardApis: ShortlistBoardPreferenceApis = {
    shortlistBoardView: createInMemoryApi(DEFAULT_SHORTLIST_BOARD_VIEW),
    resourcePickerMaximized: createInMemoryApi(false),
    resourcePickerWidth: createInMemoryApi<number | null>(null)
  }
): UserPreferencesService {
  const defaultEntityListLayout: EntityListLayoutPreference = {
    viewMode: 'list',
    tileSize: 'medium'
  };
  const useEntityListLayout = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-list-layout',
      z.record(
        z.string(),
        z
          .object({
            viewMode: z.enum(['grid', 'list']),
            // an unknown size falls back to the default and keeps the view mode
            tileSize: z.enum(TILE_SIZES).optional().catch(undefined),
            // what the Size slider stored before the Size menu
            tileMinWidthPx: z.number().optional().catch(undefined)
          })
          .transform(({ viewMode, tileSize, tileMinWidthPx }) => ({
            viewMode,
            tileSize:
              tileSize ??
              (tileMinWidthPx === undefined
                ? defaultEntityListLayout.tileSize
                : tileSizeOfMinWidth(tileMinWidthPx))
          }))
      ).parse,
      {}
    )
  );
  const callsheetsProductionQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'callsheets-production-query',
      parseCallsheetProductionQuery,
      productionQueryUtils.ofDefault()
    )
  );
  const resourcingAssignResourceQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'resourcing-assign-resource-query',
      parseResourcingAssignResourceQuery,
      resourceQueryUtils.ofDefault()
    )
  );
  const shortlistBoardView = createReadWriteApiStore(
    boardApis.shortlistBoardView
  );
  const resourcePickerMaximized = createReadWriteApiStore(
    boardApis.resourcePickerMaximized
  );
  const resourcePickerWidth = createReadWriteApiStore(
    boardApis.resourcePickerWidth
  );
  const pinboardTileSize = createReadWriteApiStore(
    createLocalStorageApi(
      'pinboard-tile-size',
      z.enum(TILE_SIZES).parse,
      DEFAULT_PINBOARD_TILE_SIZE
    )
  );
  const resourcePickerResultFields = createReadWriteApiStore(
    createLocalStorageApi<ResourcePickerResultFields>(
      'resource-picker-result-fields',
      (value): ResourcePickerResultFields =>
        z
          .object({ line: z.array(z.string()), right: z.array(z.string()) })
          .catch(DEFAULT_RESOURCE_PICKER_RESULT_FIELDS)
          .parse(value),
      DEFAULT_RESOURCE_PICKER_RESULT_FIELDS
    )
  );
  const dashboardView = createReadWriteApiStore(
    createLocalStorageApi<DashboardViewPreference>(
      'dashboard-view',
      value =>
        z
          .enum([...DASHBOARD_VIEWS, 'auto'])
          .nullable()
          .catch(null)
          .parse(value),
      null
    )
  );
  const filesPanelTileSize = createReadWriteApiStore(
    createLocalStorageApi(
      'files-panel-tile-size',
      z.enum(TILE_SIZES).parse,
      DEFAULT_FILES_PANEL_TILE_SIZE
    )
  );
  const defaultProductionsListView: ProductionsListViewPreference = {
    showResourcingBudgetColumn: false,
    budgetChartMode: 'vsPlanned'
  };
  const productionsListView = createReadWriteApiStore(
    createLocalStorageApi(
      'productions-list-view',
      value => parseProductionsListView(value, defaultProductionsListView),
      defaultProductionsListView
    )
  );
  const defaultBookingConfirmationTemplateEditor: BookingConfirmationTemplateEditorPreference =
    {
      viewMode: 'split'
    };
  const bookingConfirmationTemplateEditorSchema = z.object({
    viewMode: z.enum(['primary', 'secondary', 'split'])
  });
  const bookingConfirmationTemplateEditor = createReadWriteApiStore(
    createLocalStorageApi(
      'booking-confirmation-template-editor',
      value => {
        if (value == null) {
          return defaultBookingConfirmationTemplateEditor;
        }
        const parsed = bookingConfirmationTemplateEditorSchema.safeParse(value);
        return parsed.success
          ? parsed.data
          : defaultBookingConfirmationTemplateEditor;
      },
      defaultBookingConfirmationTemplateEditor
    )
  );
  const defaultFeatureFlagManager: FeatureFlagManagerPreference = {
    search: '',
    starredFeatureIds: [],
    browseBy: 'feature'
  };
  const featureFlagManagerPreferenceSchema = z.object({
    search: z.string(),
    starredFeatureIds: z.array(z.string()),
    browseBy: z.enum(['feature', 'workspace']).default('feature')
  });
  const featureFlagManager = createReadWriteApiStore(
    createLocalStorageApi(
      'feature-flag-manager',
      value => {
        if (value == null) {
          return defaultFeatureFlagManager;
        }
        const parsed = featureFlagManagerPreferenceSchema.safeParse(value);
        return parsed.success ? parsed.data : defaultFeatureFlagManager;
      },
      defaultFeatureFlagManager
    )
  );
  const useEntityTableColumns = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-table-columns',
      z.record(z.string(), z.array(z.string())).parse,
      {}
    )
  );
  const contactPeopleTypeFilters = createReadWriteApiStore(
    createLocalStorageApi(
      'contact-people-type-filter',
      z
        .object({
          agent: z.array(z.number()),
          organisation: z.array(z.number())
        })
        .partial()
        .catch({}).parse,
      {}
    )
  );
  const useCollapsedFormGroups = createReadWriteApiStore(
    createLocalStorageApi(
      'collapsed-form-groups',
      z.record(z.string(), z.literal(true)).parse,
      {}
    )
  );
  return {
    useShortcuts: () => {
      // TODO: Load from localStorage or backend in the future
      const shortcuts: ShortcutMapping = {
        create: {
          production: 'mod+alt+p',
          shortlist: 'mod+alt+s',
          booking: 'mod+alt+b',
          callsheet: 'mod+alt+c',
          callout: 'mod+alt+o',
          resource: 'mod+alt+r'
        }
      };

      return rd.of(shortcuts);
    },

    forEntityListLayout: key => ({
      useValue: () =>
        rd.map(
          useEntityListLayout.useValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      ensureValue: async () =>
        rd.map(
          await useEntityListLayout.ensureValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      setValue: async value =>
        useEntityListLayout.setValue({
          ...rd.getOrThrow(
            await useEntityListLayout.ensureValue(),
            'Attempted to set value when preference is not loaded'
          ),
          [key ?? 'default']: value
        })
    }),

    forEntityTableColumns: scopeKey => ({
      useValue: () =>
        rd.map(useEntityTableColumns.useValue(), data => data[scopeKey]),
      ensureValue: async () =>
        rd.map(
          await useEntityTableColumns.ensureValue(),
          data => data[scopeKey]
        ),
      setValue: async value => {
        const data = rd.getOrThrow(
          await useEntityTableColumns.ensureValue(),
          'Attempted to set entity table columns when preference is not loaded'
        );
        if (value === undefined) {
          const { [scopeKey]: _removed, ...rest } = data;
          useEntityTableColumns.setValue(rest);
          return;
        }
        useEntityTableColumns.setValue({
          ...data,
          [scopeKey]: value
        });
      }
    }),
    forContactPeopleTypeFilter: relation => ({
      useValue: () =>
        rd.map(contactPeopleTypeFilters.useValue(), data => data[relation]),
      ensureValue: async () =>
        rd.map(
          await contactPeopleTypeFilters.ensureValue(),
          data => data[relation]
        ),
      setValue: async typeIds => {
        const data = rd.getOrThrow(
          await contactPeopleTypeFilters.ensureValue(),
          'Attempted to set a contact people type filter when preference is not loaded'
        );
        const { [relation]: _previous, ...rest } = data;
        contactPeopleTypeFilters.setValue(
          typeIds === undefined ? rest : { ...rest, [relation]: typeIds }
        );
      }
    }),
    forCollapsedFormGroup: key => ({
      useValue: () =>
        rd.map(useCollapsedFormGroups.useValue(), data => data[key] === true),
      ensureValue: async () =>
        rd.map(
          await useCollapsedFormGroups.ensureValue(),
          data => data[key] === true
        ),
      setValue: async collapsed => {
        const data = rd.getOrThrow(
          await useCollapsedFormGroups.ensureValue(),
          'Attempted to set a collapsed form group when preference is not loaded'
        );
        const { [key]: _previous, ...rest } = data;
        useCollapsedFormGroups.setValue(
          collapsed ? { ...rest, [key]: true } : rest
        );
      }
    }),
    productionsListView,
    bookingConfirmationTemplateEditor,
    callsheetsProductionQuery,
    resourcingAssignResourceQuery,
    shortlistBoardView,
    resourcePickerMaximized,
    resourcePickerWidth,
    pinboardTileSize,
    resourcePickerResultFields,
    filesPanelTileSize,
    featureFlagManager,
    dashboardView
  };
}
